import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { AccordionSection, Button, Cell, H3, HeaderCell, HeaderRow, Label, ModalFactory, Row, Table, TableBody, TableHead } from 'components';
import { Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { DEFAULT_POOL_NAME, ITipCalculatorResult } from 'types';
import { buildPoolSummary, centsToDollarsDisplay, Duration, formatTimeRange, toTitleCase } from 'utils';
import PoolModal from '../PoolModal/PoolModal';

const StyledAccordionSection = styled(AccordionSection)`
  .pool-item-content {
    background: ${theme.color.neutral[50]};
    padding: 0 ${toRem(theme.space.lg)};
  }

  .pool-item-table {
    margin-bottom: ${toRem(theme.space.lg)};
  }
`;

const HeaderItemWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${toRem(theme.space['4xl'])};
  width: 100%;

  h3 {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
  }

  label {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[500]};
  }
`;

const HeaderItem = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const SegmentTime = styled(Text)`
  color: ${theme.color.neutral[500]};
`;

interface IPoolItem {
  calculatorResult: ITipCalculatorResult;
  businessDay?: string;
}

const PoolItem: React.FC<IPoolItem> = ({ calculatorResult, businessDay }) => {
  const [selectedJob, setSelectedJob] = useState<string>();
  const poolData = buildPoolSummary(calculatorResult);

  const [showPoolReportModal, hidePoolModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hidePoolModal} isActive closeOnOutSideClick={false}>
        <PoolModal pool={poolData} jobTitle={selectedJob} businessDay={businessDay} onClose={hidePoolModal} />
      </ModalFactory>
    ),
    [selectedJob, poolData, businessDay]
  );

  const renderHeader = () => (
    <HeaderItemWrapper>
      <HeaderItem data-cy="header-total-time">
        <Label>Total Time</Label>
        <H3>{new Duration(poolData.totalMinutes).toString()}</H3>
      </HeaderItem>
      <HeaderItem data-cy="header-total-tips">
        <Label>Tips & Autograts</Label>
        <H3>{centsToDollarsDisplay(poolData.totalTips)}</H3>
      </HeaderItem>
    </HeaderItemWrapper>
  );

  const onAction = (jobTitle: string) => {
    setSelectedJob(jobTitle);
    showPoolReportModal();
  };

  return (
    <StyledAccordionSection
      className="pool-item"
      dataCy="pool-item-section"
      title={poolData.daySegment || dayjs(businessDay).format('MMMM Do, YYYY')}
      subtitle={poolData.daySegment !== DEFAULT_POOL_NAME && poolData.result.start && poolData.result.end && <SegmentTime size="xsmall">{formatTimeRange(poolData.result.start, poolData.result.end)}</SegmentTime>}
      actions={renderHeader()}
      hideButton={poolData.pools.length === 0}
      open={false}
      compact
    >
      <Table data-cy="pool-item-table" className="pool-item-table">
        <TableHead>
          <HeaderRow>
            <HeaderCell data-cy="job-header">Job</HeaderCell>
            <HeaderCell data-cy="time-header">Time Worked</HeaderCell>
            <HeaderCell data-cy="amount-header">Tips & Autograts</HeaderCell>
            <HeaderCell />
          </HeaderRow>
        </TableHead>
        <TableBody>
          {poolData.pools.map((pool, idx) => (
            <Row key={idx} data-cy="table-row">
              <Cell wrapText data-label="jobTitle" minWidth={110}>
                <Text size="small" fontWeight="semibold">
                  {toTitleCase(pool.jobTitle)}
                </Text>
              </Cell>
              <Cell data-label="totalMinutes">
                <Text size="small">{new Duration(pool.totalMinutes).toString()}</Text>
              </Cell>
              <Cell data-label="totalTips">
                <Text size="small">{centsToDollarsDisplay(pool.totalTips)}</Text>
              </Cell>
              <Cell data-label="actionButton" align="right">
                <Button data-cy="pool-item-modal-link" appearance="secondary" size="small" onClick={() => onAction(pool.jobTitle)} label="Employees & Rules" />
              </Cell>
            </Row>
          ))}
        </TableBody>
      </Table>
    </StyledAccordionSection>
  );
};

export default PoolItem;
